/**
 * algorithm-step.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { Observable } from "rxjs/Rx";

import { MessageReceiver } from "../../server/message-receiver";
import { MessageTag } from "../../server/messages/";
import { NextStepsResponse, RestartAlgorithmBroadcast }
  from "../../server/messages/algorithms/";
import { StepDefinitionsRequest }
  from "../../server/messages/algorithms/graphs";
import { MessagingService } from "../../server/messaging.service";

import { AlgorithmStep, IntakeAnswer, IntakeStep } from "../structure";
import { StepFacade } from "./step.facade";

import { AlertService } from "../../common/alert.service";
import { AlgorithmService } from "./algorithm.service";

/**
 * The {@link AlgorithmStepService} is the generic implementation of
 * {@link StepFacade}, allowing for retrieval and monitoring of registered
 * {@link AlgorithmStep}s of any type.
 *
 * Unlike the other facades (e.g. {@link IntakeStepService}), this service does
 * not handle any incoming step data. Its only server interactions are limited
 * to definition requests.
 */
@Injectable()
export class AlgorithmStepService extends StepFacade<AlgorithmStep>
  implements MessageReceiver
{
  private static BadDataResetMessage : string =
    `A user provided a bad or malformed response. In order to protect data
    integrity, the algorithm has been reset to its last saved state. If you
    were making changes, please inspect for data loss.`;

  /**
   * Build the {@link AlgorithmStepService} by injecting dependencies into its
   * parent class.
   * @param messaging dependency-injection
   * @param context dependency-injection
   */
  public constructor (
    public messaging : MessagingService,
    public algorithms : AlgorithmService,
    public alerts : AlertService
  )
  {
    super(messaging, algorithms);
  }

  /**
   * Instruct {@link StepFacade} what instances are handled by this service.
   * @param step instance to check
   */
  protected matchesSubtype (step : AlgorithmStep) : step is AlgorithmStep
  {
    return true;
  }

  /**
   * Listen for {@link SubjectContextRefreshMessage}s and send global step
   * deactivations when they are received.
   *
   * NOTE: We handle this in {@link AlgorithmStepService} because refresh will
   * apply to all step types, and so shouldn't live in the specific classes.
   */
  public connected () : void
  {
    this.subscriptions.add(
      this.messaging.receive(MessageTag.BCST_RESTART_ALGORITHM)
        .subscribe((msg : RestartAlgorithmBroadcast) => {
          if (msg.maybeLostData)
          {
            this.alerts.error(AlgorithmStepService.BadDataResetMessage);
          }
          this.assertRunning();
          this.algorithms.running.steps.deactivateAll();
          console.warn("Deactivated all steps");
        }));
  }
  /**
   * No-op necessary to satisfy {@link StepFacade}.
   */
  public disconnected () : void {/* decorator-generated */}

  /**
   * Get all of the currently registered {@link AlgorithmStep}s.
   */
  public all () : AlgorithmStep[]
  {
    this.assertRunning();
    return this.algorithms.running.steps.all();
  }

  /**
   * Get a speicific {@link AlgorithmStep} by its ID.
   * @param id
   */
  public get (id : number) : AlgorithmStep
  {
    this.assertRunning();
    return this.algorithms.running.steps.get(id);
  }

  /**
   * Mark all registered steps as inactive.
   */
  private deactivateAll () : void
  {
    this.assertRunning();
    this.algorithms.running.steps.all()
      .forEach((step : IntakeStep) => step.isActive = false);
  }

  /**
   * Request definitions from the server for a list of unrecognized step IDs.
   * @param ids IDs of unknown steps
   */
  public requestDefinitions (ids : number[]) : Observable<AlgorithmStep[]>
  {
    return this.messaging.send(new StepDefinitionsRequest(ids)).map(
      (nsm : NextStepsResponse) => {
        const steps : AlgorithmStep[] = NextStepsResponse.InstantiateSteps(nsm);
        if (this.isRunning())
        {
          steps.forEach((step : AlgorithmStep) =>
            this.algorithms.running.steps.define(step)
          );
        }
        return steps;
      });
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/algorithms/management/algorithm-step.service.ts